import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from './services/cloudflare-api';

const EditModeContext = createContext();

export const useEditMode = () => {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  return context;
};

export const EditModeProvider = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPage, setEditingPage] = useState(null); // 'service', 'home', 'about', etc.
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [contentChanges, setContentChanges] = useState({});
  const [servicePageCache, setServicePageCache] = useState({}); // Cache for D1 service page content

  // Enable edit mode for a specific page
  const enableEditMode = useCallback((pageType, serviceId = null) => {
    setIsEditMode(true);
    setEditingPage(pageType);
    setEditingServiceId(serviceId);
    setHasUnsavedChanges(false);
    setContentChanges({});
  }, []);

  // Helper to check for unsaved changes across all sources
  const checkForUnsavedChanges = useCallback(() => {
    // Check static field changes
    if (Object.keys(contentChanges).length > 0) {
      return true;
    }

    // Check localStorage buffers if we have a serviceId
    if (editingServiceId) {
      const slidesBuffer = localStorage.getItem(`slides_buffer_${editingServiceId}`);
      const slidesDeleted = localStorage.getItem(`slides_deleted_${editingServiceId}`);
      const featuresBuffer = localStorage.getItem(`features_buffer_${editingServiceId}`);
      const featuresDeleted = localStorage.getItem(`features_deleted_${editingServiceId}`);
      const stepsBuffer = localStorage.getItem(`stepcontainer_buffer_${editingServiceId}`);
      const stepsDeleted = localStorage.getItem(`stepcontainer_deleted_${editingServiceId}`);
      const comparisonFeaturesBuffer = localStorage.getItem(`comparison_features_buffer_${editingServiceId}`);
      const comparisonFeaturesDeleted = localStorage.getItem(`comparison_features_deleted_${editingServiceId}`);
      const slidegalleryBuffer = localStorage.getItem(`slidegallery_buffer_${editingServiceId}`);
      const slidegalleryDeleted = localStorage.getItem(`slidegallery_deleted_${editingServiceId}`);
      const welcomeareaBuffer = localStorage.getItem(`welcomearea_backgrounds_${editingServiceId}`);

      if (slidesBuffer || slidesDeleted || featuresBuffer || featuresDeleted ||
          stepsBuffer || stepsDeleted || comparisonFeaturesBuffer || comparisonFeaturesDeleted ||
          slidegalleryBuffer || slidegalleryDeleted || welcomeareaBuffer) {
        return true;
      }
    }

    return false;
  }, [contentChanges, editingServiceId]);

  // Monitor for changes and update hasUnsavedChanges state
  useEffect(() => {
    if (isEditMode) {
      const hasChanges = checkForUnsavedChanges();
      setHasUnsavedChanges(hasChanges);
    }
  }, [isEditMode, contentChanges, editingServiceId, checkForUnsavedChanges]);

  // Listen to contentUpdated events to refresh unsaved changes state
  useEffect(() => {
    const handleContentUpdate = () => {
      if (isEditMode) {
        const hasChanges = checkForUnsavedChanges();
        setHasUnsavedChanges(hasChanges);
      }
    };

    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, [isEditMode, checkForUnsavedChanges]);

  // Update content for a specific element
  const updateContent = useCallback(async (elementId, newValue, targetServiceId = null) => {
    // Use targetServiceId if provided, otherwise fall back to editingServiceId
    const effectiveServiceId = targetServiceId || editingServiceId;

    // console.log('🔧 updateContent called:', {
    //   elementId,
    //   targetServiceId,
    //   editingServiceId,
    //   effectiveServiceId
    // });

    setContentChanges(prev => ({
      ...prev,
      [elementId]: newValue
    }));
    setHasUnsavedChanges(true);

    // Check if this is welcomearea_title for a service page - use D1
    if ((editingPage === 'service' || editingPage === 'category') && effectiveServiceId && elementId === 'welcomearea_title') {
      try {
        // console.log('💾 Saving to D1:', { serviceId: effectiveServiceId, elementId, newValue });
        // await api.updateServicePageContent(effectiveServiceId, {
        //   [elementId]: newValue
        // });
        // console.log('✅ Saved to D1 successfully');

        // Dispatch event to notify components
        window.dispatchEvent(new CustomEvent('contentUpdated', {
          detail: {
            page: editingPage,
            serviceId: effectiveServiceId,
            key: elementId
          }
        }));
        return;
      } catch (error) {
        console.error('❌ Error saving to D1, falling back to localStorage:', error);
        // Fall through to localStorage as backup
      }
    }

    // For all other content, use localStorage
    let storageKey = 'servicePageContent';
    if ((editingPage === 'service' || editingPage === 'category') && effectiveServiceId) {
      storageKey = `servicePageContent_${effectiveServiceId}`;
    } else if (editingPage === 'home') {
      storageKey = 'homePageContent';
    }

    try {
      const existingContent = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const updatedContent = {
        ...existingContent,
        [elementId]: newValue,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(updatedContent));

      // Dispatch event with the specific key that was updated
      window.dispatchEvent(new CustomEvent('contentUpdated', {
        detail: {
          page: editingPage,
          serviceId: effectiveServiceId,
          key: elementId
        }
      }));
    } catch (error) {
      console.error('Error updating content:', error);
    }
  }, [editingPage, editingServiceId]);

  // Clear specific content change (for items saved directly to D1)
  const clearContentChange = useCallback((elementId) => {
    // console.log('🧹 Clearing content change for:', elementId);

    // Clear from contentChanges state
    setContentChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[elementId];
      return newChanges;
    });

    // Also clear from localStorage if it exists there
    if ((editingPage === 'service' || editingPage === 'category') && editingServiceId) {
      const storageKey = `servicePageContent_${editingServiceId}`;
      try {
        const existingContent = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (existingContent[elementId]) {
          delete existingContent[elementId];
          localStorage.setItem(storageKey, JSON.stringify(existingContent));
          // console.log('🧹 Also cleared from localStorage:', storageKey);
        }
      } catch (error) {
        console.error('Error clearing from localStorage:', error);
      }
    }

    // Note: hasUnsavedChanges will be updated by the useEffect that monitors contentChanges
  }, [editingPage, editingServiceId]);

  // Helper to calculate localStorage usage
  const getLocalStorageSize = () => {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return (total / 1024).toFixed(2); // Return size in KB
  };

  // Save all changes
  const saveChanges = useCallback(async () => {
    // console.log('saveChanges called', { editingPage, editingServiceId, contentChanges });
    // console.log('Current localStorage usage:', getLocalStorageSize(), 'KB');

    if (!editingPage) {
      console.error('Cannot save: editingPage is not set', { editingPage, editingServiceId });
      return false;
    }

    try {
      // Handle D1 save for service page fields OR category-level comparison fields
      if ((editingPage === 'service' || editingPage === 'category') && editingServiceId) {
        // List of D1-backed fields for service pages
        const d1BackedFields = [
          // WelcomeArea fields
          'welcomearea_title',
          'welcomearea_subtitle',
          'welcomearea_category',
          'welcomearea_description',
          'welcomearea_cta1',
          'welcomearea_pricing',
          // DesignerText fields
          'designertext_subtitle',
          'designertext_title1',
          'designertext_title2',
          'designertext_description',
          // Comparison section static fields
          'comparison_title',
          // BeforeAfterSlider images
          'before_image',
          'after_image'
        ];

        // Dynamically add comparison service fields for up to 10 columns
        for (let i = 1; i <= 10; i++) {
          d1BackedFields.push(
            `comparison_service${i}_title`,
            `comparison_service${i}_subtitle1`,
            `comparison_service${i}_subtitle2`,
            `comparison_service${i}_pricing`,
            `comparison_service${i}_fineprint`,
            `comparison_service${i}_learn_more_link`,
            `comparison_service${i}_image`
          );
        }

        // Add comparison_enabled toggle field
        d1BackedFields.push('comparison_enabled');

        // Load from localStorage and filter to only include D1-backed fields
        const storageKey = `servicePageContent_${editingServiceId}`;
        const localStorageContent = JSON.parse(localStorage.getItem(storageKey) || '{}');

        const d1Changes = {};
        d1BackedFields.forEach(field => {
          // Check both contentChanges (in-memory) and localStorage
          if (contentChanges[field] !== undefined) {
            d1Changes[field] = contentChanges[field];
          } else if (localStorageContent[field] !== undefined) {
            d1Changes[field] = localStorageContent[field];
          }
        });

        console.log('💾 Preparing to save for service:', editingServiceId);
        console.log('📦 contentChanges:', contentChanges);
        console.log('📦 localStorage content:', localStorageContent);
        console.log('📦 d1Changes to save:', d1Changes);

        // Save D1-backed fields if any changes
        if (Object.keys(d1Changes).length > 0) {
          // console.log('💾 Saving service page fields to D1 for service:', editingServiceId);
          // console.log('📝 Fields to save:', Object.keys(d1Changes));

          try {
            await api.updateServicePageContent(editingServiceId, d1Changes);
            // console.log('✅ Saved service page fields to D1 successfully');

            // Update cache with all changes
            setServicePageCache(prev => ({
              ...prev,
              [editingServiceId]: {
                ...prev[editingServiceId],
                ...d1Changes
              }
            }));

            // Clear saved fields from contentChanges state
            setContentChanges(prev => {
              const updated = { ...prev };
              Object.keys(d1Changes).forEach(key => {
                delete updated[key];
              });
              return updated;
            });
            // console.log('🧹 Cleared saved fields from contentChanges');
          } catch (d1Error) {
            console.error('❌ Error saving to D1:', d1Error);
            alert('Failed to save changes to database. Please try again.');
            return false;
          }
        }

        // Save WhyChoose features from localStorage buffer to D1
        const featuresBuffer = localStorage.getItem(`features_buffer_${editingServiceId}`);
        const featuresDeleted = localStorage.getItem(`features_deleted_${editingServiceId}`);

        if (featuresBuffer || featuresDeleted) {
          // console.log('💾 Saving WhyChoose features to D1 for service:', editingServiceId);

          try {
            // Handle deletions first
            if (featuresDeleted) {
              const deletedIds = JSON.parse(featuresDeleted);
              for (const featureId of deletedIds) {
                await api.deleteWhyChooseFeature(featureId);
                // console.log(`🗑️ Deleted feature ${featureId}`);
              }
            }

            // Handle creates and updates
            if (featuresBuffer) {
              const features = JSON.parse(featuresBuffer);
              for (const feature of features) {
                if (feature._isNew || String(feature.id).startsWith('temp_')) {
                  // Create new feature
                  await api.createWhyChooseFeature(editingServiceId, {
                    icon_name: feature.iconName,
                    title: feature.title,
                    description: feature.description
                  });
                  // console.log(`➕ Created feature: ${feature.title}`);
                } else {
                  // Update existing feature
                  await api.updateWhyChooseFeature(feature.id, {
                    icon_name: feature.iconName,
                    title: feature.title,
                    description: feature.description
                  });
                  // console.log(`✏️ Updated feature ${feature.id}`);
                }
              }
            }

            // Clear buffers
            localStorage.removeItem(`features_buffer_${editingServiceId}`);
            localStorage.removeItem(`features_deleted_${editingServiceId}`);
            // console.log('✅ Saved WhyChoose features to D1 successfully');
          } catch (error) {
            console.error('❌ Error saving WhyChoose features:', error);
            alert('Failed to save feature changes. Please try again.');
            return false;
          }
        }

        // Save slides from localStorage buffer to D1
        const slidesBuffer = localStorage.getItem(`slides_buffer_${editingServiceId}`);
        const slidesDeleted = localStorage.getItem(`slides_deleted_${editingServiceId}`);

        if (slidesBuffer || slidesDeleted) {
          // console.log('💾 Saving slides to D1 for service:', editingServiceId);

          try {
            // Handle deletions first
            if (slidesDeleted) {
              const deletedIds = JSON.parse(slidesDeleted);
              for (const slideId of deletedIds) {
                await api.deleteSlide(slideId);
                // console.log(`🗑️ Deleted slide ${slideId}`);
              }
            }

            // Handle creates and updates
            if (slidesBuffer) {
              const slides = JSON.parse(slidesBuffer);
              for (const slide of slides) {
                if (slide._isNew || String(slide.id).startsWith('temp_')) {
                  // Create new slide
                  await api.createSlide(editingServiceId, {
                    title: slide.title,
                    description: slide.description,
                    image_url: slide.image,
                    bg_color: slide.bgColor
                  });
                  // console.log(`➕ Created slide: ${slide.title}`);
                } else {
                  // Update existing slide
                  await api.updateSlide(slide.id, {
                    title: slide.title,
                    description: slide.description,
                    image_url: slide.image,
                    bg_color: slide.bgColor
                  });
                  // console.log(`✏️ Updated slide ${slide.id}`);
                }
              }
            }

            // Clear buffers
            localStorage.removeItem(`slides_buffer_${editingServiceId}`);
            localStorage.removeItem(`slides_deleted_${editingServiceId}`);
            // console.log('✅ Saved slides to D1 successfully');
          } catch (error) {
            console.error('❌ Error saving slides:', error);
            alert('Failed to save slide changes. Please try again.');
            return false;
          }
        }

        // Save StepContainer steps from localStorage buffer to D1
        const stepsBuffer = localStorage.getItem(`stepcontainer_buffer_${editingServiceId}`);
        const stepsDeleted = localStorage.getItem(`stepcontainer_deleted_${editingServiceId}`);

        if (stepsBuffer || stepsDeleted) {
          // console.log('💾 Saving StepContainer steps to D1 for service:', editingServiceId);

          try {
            // Handle deletions first
            if (stepsDeleted) {
              const deletedIds = JSON.parse(stepsDeleted);
              for (const stepId of deletedIds) {
                await api.deleteStepContainerStep(stepId);
                // console.log(`🗑️ Deleted step ${stepId}`);
              }
            }

            // Handle creates and updates
            if (stepsBuffer) {
              const steps = JSON.parse(stepsBuffer);
              for (const step of steps) {
                try {
                  if (step._isNew || String(step.id).startsWith('temp_')) {
                    // Create new step
                    await api.createStepContainerStep(editingServiceId, {
                      title: step.title,
                      description: step.description || null,
                      icon_name: step.icon,
                      color: step.isMainButton ? step.color : null,
                      image_url: step.image
                    });
                    // console.log(`➕ Created step: ${step.title}`);
                  } else {
                    // Update existing step
                    await api.updateStepContainerStep(step.id, {
                      title: step.title,
                      description: step.description || null,
                      icon_name: step.icon,
                      color: step.isMainButton ? step.color : null,
                      image_url: step.image
                    });
                    // console.log(`✏️ Updated step ${step.id}`);
                  }
                } catch (stepError) {
                  // If step doesn't exist (404), log warning but continue
                  console.warn(`⚠️ Could not save step ${step.id}:`, stepError.message);
                  // Don't fail the entire save operation
                }
              }
            }

            // Clear buffers
            localStorage.removeItem(`stepcontainer_buffer_${editingServiceId}`);
            localStorage.removeItem(`stepcontainer_deleted_${editingServiceId}`);

            // Also clear step image keys from servicePageContent
            const serviceContentKey = `servicePageContent_${editingServiceId}`;
            const serviceContent = JSON.parse(localStorage.getItem(serviceContentKey) || '{}');
            Object.keys(serviceContent).forEach(key => {
              if (key.match(/^stepcontainer_step\d+_image$/)) {
                delete serviceContent[key];
              }
            });
            localStorage.setItem(serviceContentKey, JSON.stringify(serviceContent));

            // Also clear from contentChanges state
            setContentChanges(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(key => {
                if (key.match(/^stepcontainer_step\d+_image$/)) {
                  delete updated[key];
                }
              });
              return updated;
            });

            // console.log('✅ Saved StepContainer steps to D1 successfully');
          } catch (error) {
            console.error('❌ Error saving StepContainer steps:', error);
            alert('Failed to save stepcontainer changes. Please try again.');
            return false;
          }
        }

        // Save Comparison features from localStorage buffer to D1
        const comparisonFeaturesBuffer = localStorage.getItem(`comparison_features_buffer_${editingServiceId}`);
        const comparisonFeaturesDeleted = localStorage.getItem(`comparison_features_deleted_${editingServiceId}`);

        if (comparisonFeaturesBuffer || comparisonFeaturesDeleted) {
          // console.log('💾 Saving Comparison features to D1 for service:', editingServiceId);

          try {
            // Handle deletions first
            if (comparisonFeaturesDeleted) {
              const deletedIds = JSON.parse(comparisonFeaturesDeleted);
              for (const featureId of deletedIds) {
                await api.deleteComparisonFeature(featureId);
                // console.log(`🗑️ Deleted comparison feature ${featureId}`);
              }
            }

            // Handle creates and updates
            if (comparisonFeaturesBuffer) {
              const featuresData = JSON.parse(comparisonFeaturesBuffer);

              // Process all 3 columns
              for (let columnNumber = 1; columnNumber <= 3; columnNumber++) {
                const columnKey = `column${columnNumber}`;
                const features = featuresData[columnKey] || [];

                for (const feature of features) {
                  if (String(feature.id).startsWith('new_')) {
                    // Create new feature
                    await api.createComparisonFeature(editingServiceId, {
                      column_number: columnNumber,
                      title: feature.title,
                      description: feature.description || null,
                      icon_type: feature.iconType,
                      icon_name: feature.iconName || null,
                      display_order: features.indexOf(feature)
                    });
                    // console.log(`➕ Created comparison feature: ${feature.title} (column ${columnNumber})`);
                  } else {
                    // Update existing feature
                    await api.updateComparisonFeature(feature.id, {
                      title: feature.title,
                      description: feature.description || null,
                      icon_type: feature.iconType,
                      icon_name: feature.iconName || null
                    });
                    // console.log(`✏️ Updated comparison feature ${feature.id}`);
                  }
                }
              }
            }

            // Clear buffers
            localStorage.removeItem(`comparison_features_buffer_${editingServiceId}`);
            localStorage.removeItem(`comparison_features_deleted_${editingServiceId}`);
            // console.log('✅ Saved Comparison features to D1 successfully');
          } catch (error) {
            console.error('❌ Error saving Comparison features:', error);
            alert('Failed to save comparison features. Please try again.');
            return false;
          }
        }

        // Save SlideGallery items from localStorage buffer to D1
        const slidegalleryBuffer = localStorage.getItem(`slidegallery_buffer_${editingServiceId}`);
        const slidegalleryDeleted = localStorage.getItem(`slidegallery_deleted_${editingServiceId}`);

        if (slidegalleryBuffer || slidegalleryDeleted) {
          // console.log('💾 Saving SlideGallery items to D1 for service:', editingServiceId);

          try {
            // Handle deletions first
            if (slidegalleryDeleted) {
              const deletedIds = JSON.parse(slidegalleryDeleted);
              for (const itemId of deletedIds) {
                await api.deleteSlideGalleryItem(itemId);
                // console.log(`🗑️ Deleted slidegallery item ${itemId}`);
              }
            }

            // Handle creates and updates
            if (slidegalleryBuffer) {
              const items = JSON.parse(slidegalleryBuffer);
              for (const item of items) {
                try {
                  if (item._isNew || String(item.id).startsWith('temp_')) {
                    // Create new item
                    await api.createSlideGalleryItem(editingServiceId, {
                      title: item.title,
                      description: item.description,
                      media_url: item.image,
                      size: item.size
                    });
                    // console.log(`➕ Created slidegallery item: ${item.title}`);
                  } else {
                    // Update existing item
                    await api.updateSlideGalleryItem(item.id, {
                      title: item.title,
                      description: item.description,
                      media_url: item.image,
                      size: item.size
                    });
                    // console.log(`✏️ Updated slidegallery item ${item.id}`);
                  }
                } catch (itemError) {
                  // If item doesn't exist (404), log warning but continue
                  console.warn(`⚠️ Could not save slidegallery item ${item.id}:`, itemError.message);
                  // Don't fail the entire save operation
                }
              }
            }

            // Clear buffers after successful save
            localStorage.removeItem(`slidegallery_buffer_${editingServiceId}`);
            localStorage.removeItem(`slidegallery_deleted_${editingServiceId}`);

            // Also clear slidegallery image keys from contentChanges state
            setContentChanges(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(key => {
                if (key.match(/^slidegallery_item_\d+_image$/)) {
                  delete updated[key];
                }
              });
              return updated;
            });

            // console.log('✅ Saved SlideGallery items to D1 successfully');
          } catch (error) {
            console.error('❌ Error saving SlideGallery items:', error);
            alert('Failed to save slide gallery. Please try again.');
            return false;
          }
        }

        // Save WelcomeArea backgrounds from localStorage buffer to D1
        const welcomeareaBuffer = localStorage.getItem(`welcomearea_backgrounds_${editingServiceId}`);

        if (welcomeareaBuffer) {
          // console.log('💾 Saving WelcomeArea backgrounds to D1 for service:', editingServiceId);

          try {
            const backgrounds = JSON.parse(welcomeareaBuffer);

            // Prepare content object for service_page_content table
            const contentToSave = {};
            if (backgrounds.mobile) {
              contentToSave.welcomearea_mobile_bg = backgrounds.mobile;
            }
            if (backgrounds.desktop) {
              contentToSave.welcomearea_desktop_bg = backgrounds.desktop;
            }

            // Save to D1 via service-content API
            if (Object.keys(contentToSave).length > 0) {
              await api.updateServicePageContent(editingServiceId, contentToSave);
              // console.log('✅ Saved WelcomeArea backgrounds to D1');
            }

            // Clear buffer after successful save
            localStorage.removeItem(`welcomearea_backgrounds_${editingServiceId}`);

          } catch (error) {
            console.error('❌ Error saving WelcomeArea backgrounds:', error);
            alert('Failed to save welcome area backgrounds. Please try again.');
            return false;
          }
        }

        // Clear unsaved changes flag BEFORE dispatching event
        setHasUnsavedChanges(false);

        // Dispatch event after all saves complete
        window.dispatchEvent(new CustomEvent('contentUpdated', {
          detail: { page: editingPage, serviceId: editingServiceId }
        }));

        // console.log('✅ Save completed successfully');
        return true;
      }

      // For all other content, use localStorage
      let storageKey = 'servicePageContent';
      if ((editingPage === 'service' || editingPage === 'category') && editingServiceId) {
        storageKey = `servicePageContent_${editingServiceId}`;
      } else if (editingPage === 'home') {
        storageKey = 'homePageContent';
      }

      // console.log('Saving to storage key:', storageKey);

      // Load existing content or create new
      const existingContent = JSON.parse(localStorage.getItem(storageKey) || '{}');

      // Merge changes
      const updatedContent = {
        ...existingContent,
        ...contentChanges,
        lastUpdated: new Date().toISOString()
      };

      // console.log('Content to save size:', JSON.stringify(updatedContent).length / 1024, 'KB');

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(updatedContent));

      // Dispatch custom event for any listeners
      window.dispatchEvent(new CustomEvent('contentUpdated', {
        detail: { page: editingPage, serviceId: editingServiceId }
      }));

      setHasUnsavedChanges(false);
      // console.log('Save successful! New usage:', getLocalStorageSize(), 'KB');
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded!');
        console.error('Current usage:', getLocalStorageSize(), 'KB');
        console.error('This typically happens when storing large images as base64.');
        console.error('Solution: Use a backend server with database and S3 storage instead of localStorage.');
        alert('Storage quota exceeded!\n\nYou have too many images stored in localStorage (browser limit: ~5-10MB).\n\nTo continue editing:\n1. Clear some images from Assets Manager\n2. Or use smaller images\n3. Or implement backend storage (database + S3)\n\nFor now, your changes cannot be saved.');
      } else {
        console.error('Error saving changes:', error);
      }
      return false;
    }
  }, [editingPage, editingServiceId, contentChanges]);

  // Disable edit mode
  const disableEditMode = useCallback(async () => {
    const hasChanges = checkForUnsavedChanges();

    if (hasChanges) {
      const userChoice = window.confirm(
        'You have unsaved changes. Would you like to save them?\n\n' +
        'Click "OK" to save and exit.\n' +
        'Click "Cancel" to stay in edit mode.\n\n' +
        'To discard changes without saving, use the "Discard" button first.'
      );

      if (userChoice) {
        // User wants to save changes
        const saveSuccess = await saveChanges();
        if (!saveSuccess) {
          alert('Failed to save changes. Please try again or discard changes to exit.');
          return false;
        }
        // Fall through to exit
      } else {
        // User clicked Cancel - stay in edit mode
        return false;
      }
    }

    // Clear all buffers on exit
    if (editingServiceId) {
      localStorage.removeItem(`slides_buffer_${editingServiceId}`);
      localStorage.removeItem(`slides_deleted_${editingServiceId}`);
      localStorage.removeItem(`features_buffer_${editingServiceId}`);
      localStorage.removeItem(`features_deleted_${editingServiceId}`);
      localStorage.removeItem(`stepcontainer_buffer_${editingServiceId}`);
      localStorage.removeItem(`stepcontainer_deleted_${editingServiceId}`);
      localStorage.removeItem(`comparison_features_buffer_${editingServiceId}`);
      localStorage.removeItem(`comparison_features_deleted_${editingServiceId}`);
      localStorage.removeItem(`slidegallery_buffer_${editingServiceId}`);
      localStorage.removeItem(`slidegallery_deleted_${editingServiceId}`);
    }

    setIsEditMode(false);
    setEditingPage(null);
    setEditingServiceId(null);
    setHasUnsavedChanges(false);
    setContentChanges({});
    return true;
  }, [editingServiceId, checkForUnsavedChanges, saveChanges]);

  // Discard changes
  const discardChanges = useCallback(() => {
    const confirmDiscard = window.confirm('Are you sure you want to discard all changes?');
    if (confirmDiscard) {
      // Clear localStorage buffers FIRST
      if (editingServiceId) {
        localStorage.removeItem(`slides_buffer_${editingServiceId}`);
        localStorage.removeItem(`slides_deleted_${editingServiceId}`);
        localStorage.removeItem(`features_buffer_${editingServiceId}`);
        localStorage.removeItem(`features_deleted_${editingServiceId}`);
        localStorage.removeItem(`stepcontainer_buffer_${editingServiceId}`);
        localStorage.removeItem(`stepcontainer_deleted_${editingServiceId}`);
        localStorage.removeItem(`comparison_features_buffer_${editingServiceId}`);
        localStorage.removeItem(`comparison_features_deleted_${editingServiceId}`);
        // console.log('🗑️ Cleared all localStorage buffers for service:', editingServiceId);
      }

      // Clear static field changes
      setContentChanges({});
      setHasUnsavedChanges(false);

      // Trigger content reload after a microtask to ensure localStorage is cleared
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('contentUpdated', {
          detail: { page: editingPage, serviceId: editingServiceId }
        }));
        // console.log('🔄 Dispatched contentUpdated event to reload from D1');
      }, 0);

      return true;
    }
    return false;
  }, [editingServiceId, editingPage]);

  // Load service page content from D1 into cache
  const loadServicePageContentToCache = useCallback(async (serviceId) => {
    try {
      // console.log('📥 Loading D1 content for service:', serviceId);
      const content = await api.getServicePageContent(serviceId);
      // console.log('📦 Received content from API:', content);
      setServicePageCache(prev => {
        const newCache = {
          ...prev,
          [serviceId]: content
        };
        // console.log('✅ Updated cache:', newCache);
        return newCache;
      });
      // console.log('✅ Cached D1 content for', serviceId, content);
      return content;
    } catch (error) {
      console.error('❌ Error loading D1 content:', error);
      return {};
    }
  }, []);

  // Get content for a specific element
  const getContent = useCallback((elementId, defaultValue = '', serviceId = null) => {
    // First check if there are unsaved changes (only in edit mode)
    if (isEditMode && contentChanges[elementId] !== undefined) {
      return contentChanges[elementId];
    }

    // Determine which service ID to use
    const lookupServiceId = serviceId || editingServiceId;

    // Check D1 cache for all D1-backed fields on service pages
    const d1BackedFields = [
      // WelcomeArea fields
      'welcomearea_title',
      'welcomearea_subtitle',
      'welcomearea_category',
      'welcomearea_description',
      'welcomearea_cta1',
      'welcomearea_pricing',
      // DesignerText fields
      'designertext_subtitle',
      'designertext_title1',
      'designertext_title2',
      'designertext_description',
      // Comparison section static fields
      'comparison_title',
      'comparison_service1_title',
      'comparison_service1_subtitle1',
      'comparison_service1_subtitle2',
      'comparison_service1_pricing',
      'comparison_service1_fineprint',
      'comparison_service1_learn_more_link',
      'comparison_service1_image',
      'comparison_service2_title',
      'comparison_service2_subtitle1',
      'comparison_service2_subtitle2',
      'comparison_service2_pricing',
      'comparison_service2_fineprint',
      'comparison_service2_learn_more_link',
      'comparison_service2_image',
      'comparison_service3_title',
      'comparison_service3_subtitle1',
      'comparison_service3_subtitle2',
      'comparison_service3_pricing',
      'comparison_service3_fineprint',
      'comparison_service3_learn_more_link',
      'comparison_service3_image',
      // BeforeAfterSlider images
      'before_image',
      'after_image'
    ];

    if (lookupServiceId && d1BackedFields.includes(elementId)) {
      const cachedServiceContent = servicePageCache[lookupServiceId];
      // console.log(`🔍 getContent for ${elementId}:`, { lookupServiceId, cachedServiceContent, cache: servicePageCache });
      if (cachedServiceContent && cachedServiceContent[elementId] !== undefined) {
        // console.log(`✅ Returning from D1 cache:`, cachedServiceContent[elementId]);
        return cachedServiceContent[elementId];
      }
      // console.log(`⚠️ No D1 cache found, using default:`, defaultValue);
    }

    // Then check localStorage (works both in edit mode and normal mode)
    if (lookupServiceId) {
      const storageKey = `servicePageContent_${lookupServiceId}`;
      const savedContent = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (savedContent[elementId] !== undefined) {
        return savedContent[elementId];
      }
    } else if (editingPage === 'home' || elementId.startsWith('home_')) {
      // Handle home page content
      const storageKey = 'homePageContent';
      const savedContent = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (savedContent[elementId] !== undefined) {
        return savedContent[elementId];
      }
    }

    // Return default value
    return defaultValue;
  }, [isEditMode, contentChanges, editingServiceId, editingPage, servicePageCache]);

  const value = {
    isEditMode,
    editingPage,
    editingServiceId,
    hasUnsavedChanges,
    contentChanges,
    enableEditMode,
    disableEditMode,
    updateContent,
    clearContentChange,
    saveChanges,
    discardChanges,
    getContent,
    loadServicePageContentToCache
  };

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
};
